import { theme } from '@Guidelines/Theme/theme';
import type DocumentTemplateFilePreviewView from '@UI/Pages/DocumentTemplates/OrganizationDocumentTemplates/Sidebars/DocumentTemplateUploadSidebar/Views/DocumentTemplateFilePreviewView';
import { ROOT_GROUP_KEY } from '@UI/Pages/DocumentTemplates/Shared/Declaration/DeclarationGroupView';
import DeclarationGroup from '@UI/Pages/DocumentTemplates/Shared/Declaration/DeclarationGroup';
import { css } from '@emotion/react';
import { CircleCheck } from 'lucide-react';
import type * as React from 'react';
import { Message, Spinner } from '@verclam/ui-kit';

const loadingRowStyle = css`
	display: flex;
	align-items: center;
	gap: 0.5rem;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	color: ${theme.colors.inkMuted};
`;

/** Recessed block on the sidebar's surface: the declaration is a read-only report, not a form field. */
const panelStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1rem;
	padding: 0.875rem 1rem;
	border-radius: ${theme.radii.medium};
	background-color: ${theme.colors.surfaceMuted};
`;

const summaryStyle = css`
	display: flex;
	align-items: center;
	gap: 0.5rem;
	flex-wrap: wrap;
	margin: 0;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	font-weight: ${theme.fontWeights.semibold};
	color: ${theme.colors.ink};
`;

const summaryIconStyle = css`
	display: flex;
	color: ${theme.states.success.ink};
`;

const summaryDetailStyle = css`
	font-weight: ${theme.fontWeights.normal};
	color: ${theme.colors.inkMuted};
`;

/**
 * The named failure states and the sentence each one shows. `readFailed` is deliberately absent: it
 * carries the reason the read itself gave, and has no fixed sentence.
 */
const FAILURE_MESSAGES: Readonly<Record<'unreadable' | 'malformed' | 'undeclared', string>> = Object.freeze({
	unreadable: "Ce fichier n'a pas pu être lu comme un document Word (.docx).",
	malformed: 'La déclaration de variables de ce fichier est illisible.',
	undeclared: 'Ce document ne déclare aucune variable de gabarit.',
});

/** French agreement on both the noun and its participle: "1 variable déclarée", "2 variables déclarées". */
const describeFieldCount = (count: number): string => {
	return count > 1 ? `${count} variables déclarées` : `${count} variable déclarée`;
};

const describeSectionCount = (count: number): string => {
	return count > 1 ? `${count} sections` : `${count} section`;
};

type Props = {
	readonly preview: DocumentTemplateFilePreviewView;
	readonly reevaluationSectionKeys: readonly string[];
	readonly onToggleReevaluationSection: (sectionKey: string) => void;
};

/**
 * Renders every state the local preview can settle on. `declared` with zero variables is not an
 * error: the file is accepted, it simply has nothing to list. No library anomaly is ever surfaced
 * here — only these named states.
 *
 * A declared file is read section by section, each one listing the variables it holds by label and by
 * type. The internal paths (`informationsGenerales.dateDeLaRencontre`) never surface: they are what
 * the View groups on, not what the person depositing the file needs to check.
 */
const DocumentTemplateFilePreview: React.FC<Props> = ({
	preview,
	reevaluationSectionKeys,
	onToggleReevaluationSection,
}) => {
	if (preview.status === 'loading') {
		return (
			<div css={loadingRowStyle}>
				<Spinner aria-label="Lecture du fichier…" />
				<span>Lecture du fichier…</span>
			</div>
		);
	}

	if (preview.status === 'readFailed') {
		return (
			<Message severity="error" text={preview.readFailureMessage ?? "Le fichier n'a pas pu être lu."} />
		);
	}

	if (preview.status !== 'declared') {
		return <Message severity="error" text={FAILURE_MESSAGES[preview.status]} />;
	}

	if (preview.fieldCount === 0) {
		return <Message severity="info" text="Ce fichier ne déclare aucune variable." />;
	}

	const sections = preview.sections;
	const topLevelSectionCount = sections.filter((section) => section.key !== ROOT_GROUP_KEY).length;

	return (
		<div css={panelStyle}>
			<p css={summaryStyle}>
				<span css={summaryIconStyle}>
					<CircleCheck size={18} />
				</span>
				<span>{describeFieldCount(preview.fieldCount)}</span>
				<span css={summaryDetailStyle}>· {describeSectionCount(topLevelSectionCount)}</span>
			</p>

			{sections.map((section) => (
				<DeclarationGroup
					key={section.key}
					group={section}
					reevaluationSectionKeys={reevaluationSectionKeys}
					onToggleReevaluationSection={onToggleReevaluationSection}
				/>
			))}
		</div>
	);
};

export default DocumentTemplateFilePreview;
